import { useState } from 'react'
import { LogOut, ChevronDown, Info } from 'lucide-react'
import { useAuth } from '@/contexts/AuthContext'
import {
  Button,
  Avatar,
  AvatarFallback,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@atrvd/saas-ui'
import { getAppVersion, getBuildDate, formatBuildDate, formatVersionLabel } from '@/lib/build-info'
import { AboutBuildModal } from './AboutBuildModal'

/**
 * User dropdown menu component
 * Displays user avatar with initials and email
 * Provides sign out option and, below it, an "About this build" entry (DCP-294)
 * that opens the version/changelog dialog.
 */
export function UserMenu() {
  const { user, signOut } = useAuth()
  // Owns the About dialog state here (not inside the dropdown) so the dialog
  // survives the dropdown unmounting when its trigger item is selected.
  const [isAboutOpen, setIsAboutOpen] = useState(false)

  /**
   * Generate user initials from email
   * Takes first 2 characters before @ symbol
   */
  const initials = user?.email
    ?.split('@')[0]
    .slice(0, 2)
    .toUpperCase() || 'JD'

  // "vX.Y.Z · <date>" label for the version menu item.
  const versionLabel = formatVersionLabel(getAppVersion(), formatBuildDate(getBuildDate()))

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" className="flex items-center gap-2">
            <Avatar className="h-8 w-8">
              <AvatarFallback className="text-xs">{initials}</AvatarFallback>
            </Avatar>
            <span className="text-sm">{user?.email || 'User'}</span>
            <ChevronDown className="h-4 w-4 text-muted-foreground" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-56">
          <DropdownMenuLabel>My Account</DropdownMenuLabel>
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={() => signOut()}>
            <LogOut className="mr-2 h-4 w-4" />
            Sign out
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          {/* App version + changelog (DCP-294) — sits below Sign out. */}
          <DropdownMenuItem onClick={() => setIsAboutOpen(true)}>
            <Info className="mr-2 h-4 w-4" />
            {versionLabel}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <AboutBuildModal isOpen={isAboutOpen} onClose={() => setIsAboutOpen(false)} />
    </>
  )
}
